import firebase from '@/firebase'
import errorLogger from '@/services/errorLogger'
import i18n, { loadLanguageAsync } from '@/i18n'

const auth = firebase.auth
const authProperty = firebase.authProperty
const db = firebase.db
const functions = firebase.functions
var recaptchaVerifier = new authProperty.RecaptchaVerifier('recaptcha-container', { size: 'invisible' })

// Debug logging utility for Vuex modules
const debugLog = (...args) => {
  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('debug') === 'true') {
      console.log(...args)
    }
  }
}

const state = {
  uid: '',
  // userToken removed - custom claims no longer used
  firstName: '',
  lastName: '',
  email: '',
  clients: [],
  groups: [],
  references: [],
  groupReferences: [],
  ekrms: [],
  groupEkrms: [],
  exportTargets: [],
  isAdmin: false,
  isManager: false,
  isScientist: false,
  isSupervisor: false,
  isUploader: false,
  isCustomizer: false,
  createdBy: '',
  isLoggedIn: false,
  initialized: false,
  password: null,
  notifications: [],
  news: [],
  unsubscribeNotificationsListener: function () {},
  readNewsPosts: [], // Array to store read news post IDs
  redirectUrl: null,
  acceptedTerms: false,
  metadataRoles: [],
  languagePreference: 'en',
  // Dev-only ngtorch branch/commit this admin has pinned. Null unless one is active.
  ngtorchOverride: null
}

const getters = {
  authUser: state => {
    return {
      uid: state.uid,
      // userToken removed - custom claims no longer used
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      clients: state.clients,
      groups: state.groups,
      references: state.references,
      groupReferences: state.groupReferences,
      ekrms: state.ekrms,
      groupEkrms: state.groupEkrms,
      exportTargets: state.exportTargets,
      isAdmin: state.isAdmin,
      isManager: state.isManager,
      isScientist: state.isScientist,
      isSupervisor: state.isSupervisor,
      isUploader: state.isUploader,
      isCustomizer: state.isCustomizer,
      createdBy: state.createdBy,
      isLoggedIn: state.isLoggedIn,
      initialized: state.initialized,
      password: state.password,
      readNewsPosts: state.readNewsPosts,
      acceptedTerms: state.acceptedTerms,
      metadataRoles: state.metadataRoles
    }
  },
  notifications: state => {
    return state.notifications
  },
  news: state => {
    return state.news
  },
  redirectUrl: state => {
    return state.redirectUrl
  },
  isEkrmManager: state => {
    return state.metadataRoles.includes('ekrm_manager')
  },
  isDataUploader: state => {
    return state.metadataRoles.includes('data_uploader')
  },
  isModelAccessManager: state => {
    return state.metadataRoles.includes('model_access_manager')
  },
  shouldShowOnlyModelReport: state => {
    return state.metadataRoles.includes('only_show_model_report')
  },
  isExternalUser: state => {
    return state.metadataRoles.includes('external_user')
  },
  isDiyModeler: state => {
    return state.metadataRoles.includes('diy_modeler')
  },
  languagePreference: state => {
    return state.languagePreference
  },
  ngtorchOverride: state => {
    return state.ngtorchOverride
  }
}

const actions = {
  updateLanguagePreference (context, language) {
    return new Promise(function (resolve, reject) {
      var uid = context.state.uid
      if (!uid) {
        reject(new Error('User not logged in'))
        return
      }

      db.collection('users').doc(uid).update({
        languagePreference: language
      }).then(function () {
        context.commit('setLanguagePreference', language)
        resolve()
      }).catch(function (error) {
        console.error('Error updating language preference:', error)
        errorLogger.log(error, { action: 'updateLanguagePreference', language: language })
        reject(error)
      })
    })
  },
  sendAuthToOpener (context, payload) {
    console.log('Send auth info. to: ', payload.targetIframeOrigin)
    window.opener.postMessage({
      type: 'auth',
      oidAuthorize: payload.oidAuthorize
    }, payload.targetIframeOrigin)
    window.close()
  },
  markNewsPostAsRead ({ commit }, postId) {
    commit('markNewsPostRead', postId)
  },
  acceptTermsAndConditions ({ commit }) {
    return new Promise((resolve, reject) => {
      const acceptTerms = functions.httpsCallable('acceptTermsAndConditions')
      acceptTerms().then((result) => {
        commit('setAcceptedTerms', true)
        resolve(result.data)
      }).catch((error) => {
        console.error('Error accepting terms:', error)
        errorLogger.log(error, { action: 'acceptTermsAndConditions' })
        reject(error)
      })
    })
  },
  deleteNewsPost (context, postId) {
    return db.collection('news').doc(postId).delete()
      .then(() => {
        console.log('News post deleted successfully!')
      })
      .catch(error => {
        console.error('Error deleting news post:', error)
        errorLogger.log(error, { action: 'deleteNewsPost', postId })
        throw error
      })
  },
  createNewsPost (context, payload) {
    return new Promise((resolve, reject) => {
      const newsData = {
        title: payload.title,
        content: payload.content,
        profiles: [],
        createdBy: context.state.uid,
        createdByName: `${context.state.firstName} ${context.state.lastName}`,
        dateCreated: firebase.firestore.FieldValue.serverTimestamp()
      }
      if (payload.profileViewer) {
        newsData.profiles.push('viewer')
      }
      if (payload.profileCustomizer) {
        newsData.profiles.push('customizer')
      }
      if (payload.profileManager) {
        newsData.profiles.push('manager')
      }
      db.collection('news').add(newsData)
        .then(() => {
          resolve()
        })
        .catch(error => {
          console.error('Error creating news post:', error)
          reject(error)
        })
    })
  },
  clearAllNotifications (context) {
    return new Promise((resolve, reject) => {
      let batch = db.batch()
      const notifications = context.state.notifications.map(n => n.id)
      notifications.forEach(notificationId => {
        batch.set(db.collection('notifications_users').doc(context.state.uid).collection('notifications').doc(notificationId), {
          viewed: true
        }, { merge: true })
      })
      batch.commit().then(() => {
        resolve()
      }).catch(error => {
        console.log(error)
        reject(error)
      })
    })
  },
  markNotificationAsViewed (context, payload) {
    return db.collection('notifications_users').doc(payload.uid).collection('notifications').doc(payload.notificationId).update({
      viewed: true
    })
  },
  loadNotifications (context, payload) {
    // Always unsubscribe active notifications onSnapshot listener before setting a new one (avoids adding previously active notifications to current view)
    context.state.unsubscribeNotificationsListener()
    const unsubscribeNotificationsListener = db.collection('notifications_users').doc(payload.uid).collection('notifications').where('viewed', '==', false).orderBy('date_created', 'desc').onSnapshot(snapshot => {
      const data = []
      snapshot.forEach(doc => {
        let document = {
          id: doc.id,
          ...doc.data()
        }
        try {
          // Convert from Firestore timestamp to Date
          document.date_created = document.date_created.toDate()
        } catch (error) {}
        data.push(document)
      })
      context.commit('setNotifications', {
        notifications: data
      })
    })
    context.commit('setUnsubscribeNotificationsListener', {
      unsubscribeNotificationsListener: unsubscribeNotificationsListener
    })
  },
  loadNews (context, payload) {
    return new Promise((resolve, reject) => {
      const userProfiles = ['viewer']
      if (payload && payload.isManager) {
        userProfiles.push('manager')
      }
      if (payload && payload.isCustomizer) {
        userProfiles.push('customizer')
      }
      db.collection('news').where('profiles', 'array-contains-any', userProfiles).get().then(async snapshot => {
        const news = []
        snapshot.forEach(doc => {
          news.push({
            ...doc.data(),
            id: doc.id
          })
        })
        context.commit('setNews', {
          news: news
        })
        resolve()
      })
    })
  },
  setNewPasswordByUser (context, payload) {
    return new Promise((resolve, reject) => {
      const user = auth.currentUser
      const credential = authProperty.EmailAuthProvider.credential(payload.email, payload.currentPassword)
      user.reauthenticateWithCredential(credential).then(() => {
        user.updatePassword(payload.newPassword).then(() => {
          const updateModelProcessStageStatus = functions.httpsCallable('registerPasswordChange')
          updateModelProcessStageStatus().then(() => {
            resolve()
          })
        }).catch(error => {
          reject(error)
        })
      }).catch((error) => {
        reject(error)
      })
    })
  },
  confirmPasswordReset (context, payload) {
    return new Promise((resolve, reject) => {
      if (payload.actionCode && payload.newPassword) {
        auth.confirmPasswordReset(payload.actionCode, payload.newPassword).then((response) => {
          console.log(response)
          resolve()
        }).catch(function (error) {
          reject(error)
        })
      } else {
        reject(new Error('Missing parameters'))
      }
    })
  },
  resetPassword (context, payload) {
    return new Promise((resolve, reject) => {
      if (payload.email) {
        auth.sendPasswordResetEmail(payload.email).then(function () {
          resolve()
        }).catch(function () {
          resolve()
        })
      } else {
        reject(new Error('No email provided'))
      }
    })
  },
  initAuth (context) {
    return new Promise((resolve, reject) => {
      auth.onAuthStateChanged(user => {
        if (user) {
          user.getIdToken(true)
          // Note: Custom user token claims removed - now using direct Firestore lookups in storage rules
          context.dispatch('getCurrentUser', {
            userObject: user
          }).then(() => {
            // Sync i18n with user's language preference
            // Prefer localStorage (user's recent choice) over Firestore default
            var localStorageLang = localStorage.getItem('languagePreference')
            var firestoreLang = context.getters.languagePreference
            var language = localStorageLang || firestoreLang || 'en'
            if (i18n.locale !== language) {
              loadLanguageAsync(i18n, language)
            }
            localStorage.setItem('languagePreference', language)
            context.commit('setReadNewsPostsFromLocalStorage')
            resolve(user)
          })
        } else {
          context.dispatch('getCurrentUser', {
            userObject: null
          })
          resolve()
        }
      }, reject)
    })
  },
  signIn ({ commit }, payload) {
    return auth.signInWithEmailAndPassword(payload.user, payload.password).then((profile) => {
      console.log(profile.user.uid)
    }).catch((error) => {
      if (error.code === 'auth/multi-factor-auth-required') {
        console.log('auth/multi-factor-auth-required')
        // console.log(error.resolver)
        let resolver = error.resolver
        var phoneInfoOptions = {
          multiFactorHint: resolver.hints[0],
          session: resolver.session
        }
        var phoneAuthProvider = new authProperty.PhoneAuthProvider()
        // Send SMS verification code
        return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
          .then(function (verificationId) {
            // Ask user for the SMS verification code.
            const verificationCode = prompt('Enter the code received by SMS')
            var cred = authProperty.PhoneAuthProvider.credential(verificationId, verificationCode)
            var multiFactorAssertion = authProperty.PhoneMultiFactorGenerator.assertion(cred)
            // recaptchaVerifier.clear()
            // Complete sign-in.
            return resolver.resolveSignIn(multiFactorAssertion)
          })
          .then(function (userCredential) {
            // User successfully signed in with the second factor phone number.
            console.log('Verification successful')
          })
          .catch((error) => {
            console.log(error)
            // recaptchaVerifier.clear()
            throw error
          })
      } else {
        console.log(error)
        throw error
      }
    })
  },
  signInWithGoogle (context) {
    let provider = new authProperty.GoogleAuthProvider()
    return auth
      .signInWithPopup(provider)
      .then((result) => {
        /** @type {authProperty.OAuthCredential} */
        var credential = result.credential
        // This gives you a Google Access Token. You can use it to access the Google API.
        var token = credential.accessToken
        // The signed-in user info.
        var user = result.user
        debugLog('Logged in with Google')
        debugLog(token)
        debugLog(user)
      }).catch((error) => {
        // Handle Errors here.
        var errorCode = error.code
        var errorMessage = error.message
        // The email of the user's account used.
        var email = error.email
        // The firebase.auth.AuthCredential type that was used.
        var credential = error.credential
        // ...
        console.log(errorCode)
        console.log(errorMessage)
        console.log(email)
        console.log(credential)
      })
  },
  authorizeWithOid ({ commit, state }, payload) {
    return new Promise((resolve, reject) => {
      const codeVerifier = localStorage.getItem('codeVerifier')
      console.log(codeVerifier)
      functions.httpsCallable('authorizeWithOid')({
        codeVerifier: codeVerifier,
        code: payload.query.code,
        query: payload.query
      }).then(function (result) {
        const customToken = result.data
        if (customToken) {
          // If user was validated, then Firebase generated a customToken to sign in the corresponding user
          // Here we use customToken to sign in the corresponding user
          auth.signInWithCustomToken(customToken).then(profile => {
            resolve(true)
          }).catch(function (error) {
            reject(error)
          })
        } else {
          // User couldn't be validated. Either it's not valid, or there were connection or server-side errors
          resolve(false)
        }
      }).catch(function (error) {
        reject(error)
      })
    })
  },
  signInWithOid ({ commit }, payload) {
    console.log('Payload', payload)
    return new Promise((resolve, reject) => {
      // Redirect user to OpenId provider corresponding to origin
      functions.httpsCallable('signInWithOid')({
        origin: payload.origin,
        autoclose: payload.newTab
      }).then(function (result) {
        const redirectUrl = result.data.redirectUrl
        const codeVerifier = result.data.codeVerifier
        if (redirectUrl && codeVerifier) {
          localStorage.setItem('codeVerifier', codeVerifier)
          // Redirect user (Hack: setTimeout waits for localStorage.setItem to complete. It is apparently not synchronous)
          setTimeout(function () {
            if (payload.newTab) {
              commit('setRedirectUrl', redirectUrl)
            } else {
              window.location.replace(redirectUrl)
            }
          }, 0)
        } else {
          // redirectUrl could not be generated
          console.log('Not set')
          resolve(false)
        }
      }).catch(function (error) {
        console.log('error: ', error)
        reject(error)
      })
    })
  },
  signInWithToken ({ commit }, payload) {
    return new Promise((resolve, reject) => {
      // Validate externalToken against external auth provider corresponding to origin
      functions.httpsCallable('signInWithToken')({
        externalToken: payload.externalToken,
        origin: payload.origin
      }).then(function (result) {
        const customToken = result.data
        if (customToken) {
          // If externalToken was validated, then Firebase generated a customToken to sign in the corresponding user
          // Here we use customToken to sign in the corresponding user
          auth.signInWithCustomToken(customToken).then(profile => {
            resolve(true)
          }).catch(function (error) {
            reject(error)
          })
        } else {
          // externalToken couldn't be validated. Either it's not valid, or there were connection or server-side errors
          resolve(false)
        }
      }).catch(function (error) {
        reject(error)
      })
    })
  },
  signOut () {
    return auth.signOut().catch((error) => {
      console.log(error)
    })
  },
  async debugUserPermissions (context, uid) {
    debugLog('=== DEBUGGING USER PERMISSIONS FOR UID:', uid, '===')

    try {
      // Test 1: Can read own user document
      const userDoc = await db.collection('users').doc(uid).get()
      debugLog('✓ User document readable:', userDoc.exists, 'data:', userDoc.data())
    } catch (error) {
      console.error('✗ Failed to read user document:', error)
      errorLogger.log(error, { action: 'debugUserPermissions', step: 'readUserDocument', uid })
    }

    try {
      // Test 2: Can read own permissions/client_edit_access
      const clientPermissions = await db.collection('permissions').doc('client_edit_access').collection('users').doc(uid).collection('clients').get()
      debugLog('✓ Client permissions readable, count:', clientPermissions.docs.length)
      clientPermissions.docs.forEach(doc => debugLog('  - Client permission:', doc.id))
    } catch (error) {
      console.error('✗ Failed to read client permissions:', error)
    }

    try {
      // Test 3: Can read own metadata/references
      const references = await db.collection('users').doc(uid).collection('metadata').doc('references').get()
      debugLog('✓ References metadata readable:', references.exists, 'data:', references.data())
    } catch (error) {
      console.error('✗ Failed to read references metadata:', error)
    }

    try {
      // Test 4: Can read own groups/data/users
      const groupModels = await db.collection('groups').doc('data').collection('users').doc(uid).collection('models').get()
      debugLog('✓ Group models readable, count:', groupModels.docs.length)
      groupModels.docs.forEach(doc => debugLog('  - Group model:', doc.id, 'groupCount:', doc.data().groupCount))
    } catch (error) {
      console.error('✗ Failed to read group models:', error)
    }

    debugLog('=== END PERMISSIONS DEBUG ===')
  },
  async loadUserWithoutPermissions (context, userObject) {
    debugLog('FALLBACK: Loading user without client permissions for:', userObject.uid)
    try {
      const snapshot = await db.collection('users').doc(userObject.uid).get()
      if (snapshot.exists) {
        const data = snapshot.data()
        const userData = {
          uid: userObject.uid,
          ...data,
          clients: [], // Empty clients array as fallback
          references: [],
          groupReferences: [],
          ekrms: [],
          groupEkrms: []
        }
        context.commit('setCurrentUser', userData)
        debugLog('FALLBACK: Successfully loaded basic user data')

        // Try to load notifications and news with basic permissions
        context.dispatch('loadNotifications', { uid: userData.uid })
        context.dispatch('loadNews', {
          isManager: userData.isManager,
          isCustomizer: userData.isCustomizer
        })
      }
    } catch (error) {
      console.error('FALLBACK: Even basic user loading failed:', error)
    }
  },
  getCurrentUser (context, payload) {
    if (payload.userObject) {
      // Debug permissions first
      context.dispatch('debugUserPermissions', payload.userObject.uid)

      return db.collection('users').doc(payload.userObject.uid).get().then(function (snapshot) {
        db.collection('permissions').doc('client_edit_access').collection('users').doc(payload.userObject.uid).collection('clients').get().then(itemClients => {
          debugLog('DEBUG: get() for user permissions - user:', payload.userObject.uid, 'clients found:', itemClients.docs.length)
          const data = snapshot.data()
          const clients = itemClients.docs.map(doc => doc.id)
          db.collection('users').doc(payload.userObject.uid).collection('metadata').doc('references').get().then(itemReferences => {
            let references = []
            if (typeof itemReferences.data === 'function' && itemReferences.data() && itemReferences.data().references) {
              references = itemReferences.data().references
            }
            db.collection('users').doc(payload.userObject.uid).collection('metadata').doc('ekrms').get().then(itemEkrms => {
              let ekrms = []
              if (typeof itemEkrms.data === 'function' && itemEkrms.data() && itemEkrms.data().references) {
                ekrms = itemEkrms.data().references
              }
              db.collection('users').doc(payload.userObject.uid).collection('metadata').doc('roles').get().then(itemRoles => {
                let metadataRoles = []
                if (typeof itemRoles.data === 'function' && itemRoles.data() && itemRoles.data().roles) {
                  metadataRoles = itemRoles.data().roles
                }
                db.collection('groups').doc('data').collection('users').doc(payload.userObject.uid).collection('references').where('groupCount', '>', 0).get().then(itemGroupReferences => {
                  const groupReferences = itemGroupReferences.docs.map(item => item.id)
                  db.collection('groups').doc('data').collection('users').doc(payload.userObject.uid).collection('ekrms').where('groupCount', '>', 0).get().then(itemGroupEkrms => {
                    const groupEkrms = itemGroupEkrms.docs.map(item => item.id)
                    const userData = {
                      uid: payload.userObject.uid,
                      ...data,
                      clients: clients.map(client => db.collection('clients').doc(client)),
                      references: references,
                      groupReferences: groupReferences,
                      ekrms: ekrms,
                      groupEkrms: groupEkrms,
                      metadataRoles: metadataRoles
                    }
                    context.commit('setCurrentUser', userData)
                    context.dispatch('loadNotifications', {
                      uid: userData.uid
                    })
                    context.dispatch('loadNews', {
                      isManager: userData.isManager,
                      isCustomizer: userData.isCustomizer
                    })
                  }).catch(error => {
                    console.error('ERROR: Failed to load group ekrms for user:', payload.userObject.uid, error)
                  })
                }).catch(error => {
                  console.error('ERROR: Failed to load group references for user:', payload.userObject.uid, error)
                })
              }).catch(error => {
                console.error('ERROR: Failed to load metadata roles for user:', payload.userObject.uid, error)
              })
            }).catch(error => {
              console.error('ERROR: Failed to load user ekrms metadata for user:', payload.userObject.uid, error)
            })
          }).catch(error => {
            console.error('ERROR: Failed to load user references metadata for user:', payload.userObject.uid, error)
          })
        }).catch(error => {
          console.error('ERROR: get() failed for user permissions - user:', payload.userObject.uid, 'error:', error)
          console.error('ERROR: This usually means the user lacks permission to read their own client permissions')
          console.error('ERROR: Firestore rules may be preventing access to /permissions/client_edit_access/users/{uid}/clients')
          console.error('ERROR: Will retry with fallback method...')

          // Fallback: try to load user without client permissions
          context.dispatch('loadUserWithoutPermissions', payload.userObject)
        })
      })
    } else {
      context.commit('setCurrentUser')
    }
  }
}

const mutations = {
  setNews (state, payload) {
    state.news = payload.news
  },
  setNotifications (state, payload) {
    state.notifications = payload.notifications
  },
  setUnsubscribeNotificationsListener (state, payload) {
    state.unsubscribeNotificationsListener = payload.unsubscribeNotificationsListener
  },
  setCurrentUser (state, payload) {
    if (payload) {
      state.uid = payload.uid
      state.firstName = payload.firstName
      state.lastName = payload.lastName
      state.email = payload.email
      state.clients = payload.clients
      state.groups = payload.groups
      state.references = payload.references
      state.groupReferences = payload.groupReferences
      state.ekrms = payload.ekrms
      state.groupEkrms = payload.groupEkrms
      state.exportTargets = payload.export_targets
      state.isAdmin = payload.isAdmin
      state.isScientist = payload.isScientist
      state.isManager = payload.isManager
      state.isSupervisor = payload.isSupervisor
      state.isUploader = payload.isUploader
      state.isCustomizer = payload.isCustomizer
      state.createdBy = payload.createdBy
      state.isLoggedIn = true
      state.initialized = true
      state.password = payload.password
      state.acceptedTerms = payload.acceptedTerms || false
      state.metadataRoles = payload.metadataRoles || []
      state.languagePreference = payload.languagePreference || 'en'
      state.ngtorchOverride = payload.ngtorch_override || null
    } else {
      state.isLoggedIn = false
      state.initialized = true
      state.metadataRoles = []
      state.languagePreference = 'en'
      state.ngtorchOverride = null
    }
  },
  setLanguagePreference (state, language) {
    state.languagePreference = language
  },
  setNgtorchOverride (state, payload) {
    state.ngtorchOverride = payload || null
  },
  // setCurrentUserToken removed - custom claims no longer used
  setAcceptedTerms (state, accepted) {
    state.acceptedTerms = accepted
  },
  markNewsPostRead (state, postId) {
    if (!state.readNewsPosts.includes(postId)) {
      state.readNewsPosts.push(postId)
      localStorage.setItem('readNewsPosts', JSON.stringify(state.readNewsPosts))
    }
  },
  setReadNewsPostsFromLocalStorage (state, payload) {
    const storedReadNewsPosts = localStorage.getItem('readNewsPosts')
    if (storedReadNewsPosts) {
      const parsedReadNewsPosts = JSON.parse(storedReadNewsPosts)
      state.readNewsPosts = parsedReadNewsPosts
    }
  },
  setRedirectUrl (state, redirectUrl) {
    state.redirectUrl = redirectUrl
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
